import { useMemo } from 'react';

import { atom, PrimitiveAtom, SetStateAction, useAtom } from 'jotai';
import { focusAtom } from 'jotai-optics';

import { store } from './store';

export const focusHook = <T extends Record<string, unknown>>(
  base: PrimitiveAtom<T>,
) => {
  // Returned function is a proper hook
  const useFocus = <K extends keyof T>(key: K) => {
    const slice = focusAtom(base, o => o.prop(key));
    return useAtom(slice) as [T[K], (v: SetStateAction<T[K]>) => void];
  };
  return useFocus;
};

export type FocusState<
  TState extends Record<string, unknown>,
  T extends keyof TState & string,
> = Record<T, TState[T]> &
  Record<`set${Capitalize<T>}`, (value: SetStateAction<TState[T]>) => void>;

export const useFocusState = <
  TState extends Record<string, unknown>,
  TSelect extends keyof TState & string,
>(
  stateAtom: PrimitiveAtom<TState>,
  select: TSelect,
): FocusState<TState, TSelect> => {
  // Explicity memoize focusAtom for atoms in hook.
  const derivedAtom = useMemo(
    () => focusAtom(stateAtom, optic => optic.prop(select)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [value, setValue] = useAtom(derivedAtom);

  return {
    [select]: value,
    [`set${select.charAt(0).toUpperCase() + select.slice(1)}`]: setValue,
  } as FocusState<TState, TSelect>;
};

export const focusState = <
  TState extends Record<string, unknown>,
  TSelect extends keyof TState & string,
>(
  stateAtom: PrimitiveAtom<TState>,
  select: TSelect,
): FocusState<TState, TSelect> => {
  const derivedAtom = focusAtom(stateAtom, optic => optic.prop(select));

  const setValue = (value: SetStateAction<TState[TSelect]>) => {
    store.set(derivedAtom, value);
  };

  return {
    [select]: store.get(derivedAtom),
    [`set${select.charAt(0).toUpperCase() + select.slice(1)}`]: setValue,
  } as FocusState<TState, TSelect>;
};

export const setState = <
  TState extends Record<string, unknown>,
  TSelect extends keyof TState & string,
>(
  stateAtom: PrimitiveAtom<TState>,
  select: TSelect,
  value: SetStateAction<TState[TSelect]>,
): void => {
  const derivedAtom = focusAtom(stateAtom, optic => optic.prop(select));
  store.set(derivedAtom, value);
};

/** Atom for large state management. Provides a set of functions to manage the state. */
export const stateAtom = <TState extends Record<string, unknown>>(
  initial: TState,
) => {
  const stateAtom = atom(initial);
  return {
    atom: stateAtom,
    focusState: <TSelect extends keyof TState & string>(select: TSelect) =>
      focusState(stateAtom, select),
    getState: () => store.get(stateAtom),
    setState: <TSelect extends keyof TState & string>(
      select: TSelect,
      value: SetStateAction<TState[TSelect]>,
    ) => setState(stateAtom, select, value),
    useState: () => useAtom(stateAtom),
    useFocusState: <TSelect extends keyof TState & string>(select: TSelect) =>
      useFocusState(stateAtom, select),
  };
};
